import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import { DifficultyRatingService, DifficultyRatingInfo, DifficultyMeta } from '../services/difficultyRatingService';

interface DifficultyRatingBadgeProps {
  itemId: string;
  isExam?: boolean;
  showCount?: boolean;
  compact?: boolean;
}

export const DifficultyRatingBadge: React.FC<DifficultyRatingBadgeProps> = ({
  itemId,
  isExam = false,
  showCount = true,
  compact = false,
}) => {
  const [ratingInfo, setRatingInfo] = useState<DifficultyRatingInfo>(() =>
    DifficultyRatingService.getRating(itemId, isExam)
  );

  useEffect(() => {
    const update = () => {
      setRatingInfo(DifficultyRatingService.getRating(itemId, isExam));
    };
    update();
    window.addEventListener('difficulty-ratings-updated', update);
    return () => window.removeEventListener('difficulty-ratings-updated', update);
  }, [itemId, isExam]);

  const meta: DifficultyMeta = DifficultyRatingService.getMeta(ratingInfo.avgRating);

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[8.5px] font-black border tracking-wider uppercase ${meta.badgeClass}`}>
        <Star size={10} className="fill-amber-400 text-amber-400 shrink-0" />
        <span>{ratingInfo.avgRating.toFixed(1)}</span>
        <span className="opacity-75">({meta.label})</span>
      </span>
    );
  }

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[9px] font-black border tracking-wider uppercase shadow-xs ${meta.badgeClass}`}>
      <Star size={12} className="fill-amber-400 text-amber-400 shrink-0" />
      <span className="tabular-nums font-extrabold">{ratingInfo.avgRating.toFixed(1)} / 5</span>
      <span className="font-bold opacity-80">• {meta.label}</span>
      {showCount && (
        <span className="text-[8px] font-normal opacity-70 border-l pl-1.5 border-current/20">
          {ratingInfo.count} votes
        </span>
      )}
    </div>
  );
};
